import type { ISODate, OccurrenceKey, OccurrenceOverride, RecurrenceRule, Timing } from './model.ts';
import { Temporal, addDays, compareDates, daysBetween, daysCovered } from './time.ts';
import { moveTimingToDate } from './timing.ts';

/**
 * Återkomstregler (specifikation, testad i recurrence.test.ts):
 *  - MONTHLY följer startdagens dag i månaden. Finns dagen inte (31:a i april, 30:e i februari)
 *    hoppas månaden över – den blir aldrig den 1:a i nästa månad (RFC 5545 §3.3.10).
 *  - YEARLY från 29 februari förekommer bara skottår.
 *  - Ogiltiga datum räknas inte mot COUNT.
 *  - UNTIL är ett lokalt datum och inklusive.
 *  - En tid i sommartidsglappet (t.ex. 02:30 sista söndagen i mars) flyttas fram med glappets längd
 *    och markeras adjusted. Dubbeltid på hösten tolkas som den första (tidigare) förekomsten.
 *  - EXDATE tar bort en förekomst efter att COUNT tillämpats.
 */

export interface RecurringBase {
  timing: Timing;
  recurrence?: RecurrenceRule;
  exdates?: OccurrenceKey[];
  overrides?: Record<OccurrenceKey, OccurrenceOverride>;
}

export interface Occurrence {
  /** Ursprungligt lokalt startdatum – stabil nyckel för override/klar/larmpunkt. */
  key: OccurrenceKey;
  timing: Timing;
  adjusted: boolean;
  override?: OccurrenceOverride;
  recurring: boolean;
}

export const MAX_OCCURRENCES_PER_QUERY = 5000;
const MAX_ITERATIONS = 200_000;

function baseDate(t: Timing): ISODate {
  return t.kind === 'allDay' ? t.start : t.start.slice(0, 10);
}

function spanDays(t: Timing): number {
  if (t.kind === 'allDay') return Math.max(1, daysBetween(t.start, t.endExclusive));
  return Math.max(1, daysBetween(t.start.slice(0, 10), t.end.slice(0, 10)) + 1);
}

/** Genererar förekomstdatum i ordning. Callback returnerar false för att stoppa. */
export function* recurrenceDates(start: ISODate, rule: RecurrenceRule, fromHint?: ISODate): Generator<ISODate> {
  const d0 = Temporal.PlainDate.from(start);
  const interval = Math.max(1, Math.floor(rule.interval || 1));
  const until = rule.until ? Temporal.PlainDate.from(rule.until) : null;
  let produced = 0;
  const count = rule.count ?? Infinity;
  let iterations = 0;
  const emit = (d: Temporal.PlainDate): 'stop' | 'ok' => {
    if (until && Temporal.PlainDate.compare(d, until) > 0) return 'stop';
    produced++;
    return 'ok';
  };
  // Hopp framåt när ingen COUNT finns (COUNT kräver räkning från början).
  const canJump = rule.count === undefined && fromHint !== undefined && compareDates(fromHint, start) > 0;

  if (rule.freq === 'DAILY') {
    let k = canJump ? Math.floor(daysBetween(start, fromHint!) / interval) : 0;
    while (produced < count && iterations++ < MAX_ITERATIONS) {
      const d = d0.add({ days: k * interval });
      if (emit(d) === 'stop') return;
      yield d.toString();
      k++;
    }
    return;
  }
  if (rule.freq === 'WEEKLY') {
    const days = (rule.byWeekday?.length ? [...rule.byWeekday] : [d0.dayOfWeek]).sort((a, b) => a - b);
    const week0 = d0.subtract({ days: d0.dayOfWeek - 1 });
    let w = canJump ? Math.floor(daysBetween(week0.toString(), fromHint!) / 7 / interval) : 0;
    while (produced < count && iterations++ < MAX_ITERATIONS) {
      const monday = week0.add({ days: w * 7 * interval });
      for (const dow of days) {
        const d = monday.add({ days: dow - 1 });
        if (Temporal.PlainDate.compare(d, d0) < 0) continue;
        if (emit(d) === 'stop') return;
        yield d.toString();
        if (produced >= count) return;
      }
      w++;
    }
    return;
  }
  if (rule.freq === 'MONTHLY') {
    let k = canJump ? Math.max(0, Math.floor(Temporal.PlainYearMonth.from(start).until(Temporal.PlainYearMonth.from(fromHint!.slice(0, 7)), { largestUnit: 'months' }).months / interval) - 1) : 0;
    while (produced < count && iterations++ < MAX_ITERATIONS) {
      const ym = Temporal.PlainYearMonth.from({ year: d0.year, month: d0.month }).add({ months: k * interval });
      k++;
      if (d0.day > ym.daysInMonth) {
        if (until && Temporal.PlainDate.compare(ym.toPlainDate({ day: 1 }), until) > 0) return;
        continue; // Hoppa över – aldrig rulla över till nästa månad.
      }
      const d = ym.toPlainDate({ day: d0.day });
      if (emit(d) === 'stop') return;
      yield d.toString();
    }
    return;
  }
  // YEARLY
  let k = canJump ? Math.max(0, Math.floor((Temporal.PlainDate.from(fromHint!).year - d0.year) / interval) - 1) : 0;
  while (produced < count && iterations++ < MAX_ITERATIONS) {
    const year = d0.year + k * interval;
    k++;
    const ym = Temporal.PlainYearMonth.from({ year, month: d0.month });
    if (d0.day > ym.daysInMonth) {
      if (until && year > until.year) return;
      continue; // 29 februari bara skottår.
    }
    const d = ym.toPlainDate({ day: d0.day });
    if (emit(d) === 'stop') return;
    yield d.toString();
  }
}

/**
 * Expanderar förekomster som syns inom [from, toExclusive) i visningszonen.
 * Icke-återkommande händelser ger en förekomst (key = startdatum).
 */
export function expandOccurrences(base: RecurringBase, from: ISODate, toExclusive: ISODate, displayTz: string, limit = MAX_OCCURRENCES_PER_QUERY): Occurrence[] {
  const start = baseDate(base.timing);
  const out: Occurrence[] = [];
  // Billig förfiltrering på ISO-datumsträngar (lexikografisk ordning = kronologisk) innan Temporal.
  // ±1 dag marginal täcker skillnad mellan händelsens zon och visningszonen.
  if (compareDates(start, addDays(toExclusive, 1)) > 0 && !Object.keys(base.overrides ?? {}).length) return out;
  if (!base.recurrence && !base.overrides) {
    const endDate = base.timing.kind === 'allDay' ? base.timing.endExclusive : addDays(base.timing.end.slice(0, 10), 1);
    if (compareDates(endDate, addDays(from, -1)) < 0) return out;
  }
  if (base.recurrence?.until && compareDates(addDays(base.recurrence.until, spanDays(base.timing) + 1), from) < 0 && !Object.keys(base.overrides ?? {}).length) return out;
  const intersects = (t: Timing) => {
    const days = daysCovered(t, displayTz);
    return days.some((d) => compareDates(d, from) >= 0 && compareDates(d, toExclusive) < 0);
  };
  if (!base.recurrence) {
    const ov = base.overrides?.[start];
    if (ov?.cancelled) return out;
    const timing = ov?.timing ?? base.timing;
    if (intersects(timing)) out.push({ key: start, timing, adjusted: false, recurring: false, ...(ov ? { override: ov } : {}) });
    return out;
  }
  const ex = new Set(base.exdates ?? []);
  const span = spanDays(base.timing);
  const hint = addDays(from, -span - 1);
  const overridesMovedIn = new Map<string, OccurrenceOverride>();
  for (const [k, ov] of Object.entries(base.overrides ?? {})) if (ov.timing && !ov.cancelled) overridesMovedIn.set(k, ov);
  const seen = new Set<string>();
  for (const d of recurrenceDates(start, base.recurrence, hint)) {
    if (compareDates(d, toExclusive) >= 0) break;
    if (ex.has(d)) continue;
    seen.add(d);
    // Förekomster som slutar före fönstret behöver ingen dyr tidsberäkning (överrider hanteras nedan).
    if (compareDates(addDays(d, span + 1), from) < 0 && !base.overrides?.[d]) continue;
    const ov = base.overrides?.[d];
    if (ov?.cancelled) continue;
    const shifted = d === start ? { timing: base.timing, adjusted: false } : moveTimingToDate(base.timing, d);
    const timing = ov?.timing ?? shifted.timing;
    if (!intersects(timing)) continue;
    out.push({ key: d, timing, adjusted: shifted.adjusted && !ov?.timing, recurring: true, ...(ov ? { override: ov } : {}) });
    if (out.length >= limit) break;
  }
  // Förekomster vars override flyttat in dem i fönstret från en dag utanför.
  for (const [k, ov] of overridesMovedIn) {
    if (seen.has(k) || ex.has(k)) continue;
    if (ov.timing && intersects(ov.timing) && isOccurrenceOf(base, k)) out.push({ key: k, timing: ov.timing, adjusted: false, recurring: true, override: ov });
  }
  out.sort((a, b) => compareDates(baseDate(a.timing), baseDate(b.timing)) || a.key.localeCompare(b.key));
  return out;
}

/** Är datumet en (ej exkluderad) förekomst enligt regeln? */
export function isOccurrenceOf(base: RecurringBase, key: OccurrenceKey): boolean {
  const start = baseDate(base.timing);
  if (!base.recurrence) return key === start;
  if (compareDates(key, start) < 0) return false;
  for (const d of recurrenceDates(start, base.recurrence)) {
    const c = compareDates(d, key);
    if (c === 0) return true;
    if (c > 0) return false;
  }
  return false;
}

/** Säkerhetsgräns för användarskapade serier (skydd mot enorma serier). */
export function validateRecurrence(rule: RecurrenceRule): string | null {
  if (!['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(rule.freq)) return 'Okänd frekvens.';
  if (!Number.isInteger(rule.interval) || rule.interval < 1 || rule.interval > 99) return 'Intervallet måste vara 1–99.';
  if (rule.count !== undefined && (!Number.isInteger(rule.count) || rule.count < 1 || rule.count > 1000)) return 'Antal förekomster måste vara 1–1000.';
  if (rule.byWeekday && rule.byWeekday.some((d) => d < 1 || d > 7)) return 'Ogiltig veckodag.';
  return null;
}
